import React from 'react';
import { FormattedNumber, LocaleString } from '@biw/intl';

import { getBonusReplaceOptions } from '@biw/contests-core/lib/dux/progress/utils';
import { ProgramModel } from '@biw/contests-core/lib/dux/programs';

import GoalLabel from '../goal-label';

import { getGoalCalculationLabel, checkPerPeriodValue } from './level-comparison-utils';
import PayoutBonusMessage from './partials/payout-bonus-message';
import GoalAchievementMessage from './partials/goal-achievement-message';

import './level-comparison-table.scss';

interface Props {
    goalLabel: any;
    goalLevels: any[];
    program: ProgramModel;
    baseValue: {
        goalBaseValue: number;
    };
    mediaLabel: string;
    isBaseLineRequired: boolean;
    calculationLabel: string;
    bonusPayout: any;
    mqLabel: any;

    fixedPlusRatePayoutMessageArray: any[] | null;
    challengePointBaseEarningDescription: string;

    bonusMessage: string;
    locale: any;
}

const defaultProps = {
    fixedPlusRatePayoutMessageArray: null,
    challengePointBaseEarningDescription: '',
    bonusMessage: '',
    goalLevels: [],
};

const LevelComparisonTable = ( {
    goalLabel,
    goalLevels,
    program,
    baseValue,
    mediaLabel,
    mqLabel,
    isBaseLineRequired,
    calculationLabel,
    bonusPayout,
    fixedPlusRatePayoutMessageArray,
    challengePointBaseEarningDescription,
    bonusMessage,
    locale,
}: Props ) => {
    const {
        goalPrecision,
        goalRoundingMethod,
        mqPrecision,
        mqRoundingMethod,
        isMinimumQualifierEnabled,
        isBonusEnabled,
        bonusPrecision,
        bonusRoundingMethod,
    } = program;

    let bonusReplaceOptions = {};

    if ( isBonusEnabled && bonusPayout ) {
        bonusReplaceOptions = getBonusReplaceOptions( {
            bonusPayout,
            locale,
            mediaLabel,
            bonusPrecision,
            bonusRoundingMethod,
        } ) || {};
    }

    return (
        <table className="level-comparison-table">
            <thead>
                <tr>
                    <th/>
                    {goalLevels.map( ( lvl, i ) => <th key={i} dangerouslySetInnerHTML={{ __html: lvl.name }}/> )}
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><LocaleString code="levelComparisonTable.header.description" /></td>
                    {goalLevels.map( ( lvl, i ) => <td key={i}>{lvl.description}</td> )}
                </tr>

                { isBaseLineRequired &&
                    <tr>
                        <td><LocaleString code="levelComparisonTable.header.baseline" /></td>
                        <td colSpan={3} className="baseline-amount">
                            <GoalLabel
                                {...goalLabel}
                                amount={baseValue.goalBaseValue}
                                precision={goalPrecision}
                                rounding={goalRoundingMethod}
                            />
                        </td>
                    </tr>
                }

                { !!challengePointBaseEarningDescription && // an empty string will cause dom validation issues
                    <tr>
                        <td><LocaleString code="progress.baseEarningHeader"/></td>
                        <td colSpan={5} dangerouslySetInnerHTML={{ __html: challengePointBaseEarningDescription }}/>
                    </tr>
                }
                <tr>
                    <td><LocaleString code="levelComparisonTable.header.goal" /></td>
                    { goalLevels.map( ( lvl, i ) =>
                        <td key={i}>
                            <GoalLabel
                                {...goalLabel}
                                amount={lvl.goalAchieveAmount}
                                precision={goalPrecision}
                                rounding={goalRoundingMethod}
                                key={i}
                            />
                            { checkPerPeriodValue( lvl.goalCalculationValue, goalPrecision ) &&
                                <p className="per-period">
                                    &#40;
                                    <GoalLabel
                                        {...getGoalCalculationLabel( goalLabel )}
                                        amount={checkPerPeriodValue( lvl.goalCalculationValue, goalPrecision )}
                                        precision={goalPrecision}
                                        rounding={goalRoundingMethod}
                                    />
                                    &#47;
                                    {calculationLabel}
                                    &#41;
                                </p>
                            }
                        </td>,
                    ) }
                </tr>
                <tr>
                    <td><LocaleString code="levelComparisonTable.header.potentialAward" /></td>
                    {goalLevels.map( ( lvl, i ) => <td key={i}><FormattedNumber value={lvl.goalAwardAmount || 0} /> {mediaLabel}</td> )}
                </tr>
                { fixedPlusRatePayoutMessageArray &&
                    <tr>
                        <td><LocaleString code="common.fixedRate.acceleratorHeader"/></td>
                        { fixedPlusRatePayoutMessageArray.map( ( str, i ) =>
                            <td key={i} dangerouslySetInnerHTML={{ __html: str }}/>,
                        ) }
                    </tr>
                }
                { isMinimumQualifierEnabled &&
                    <tr>
                        <td><LocaleString code="levelComparisonTable.header.minimumQualifier" /></td>
                        { goalLevels.map( ( lvl, i ) => (
                            <td key={i}>
                                <GoalLabel
                                    {...mqLabel}
                                    precision={mqPrecision}
                                    rounding={mqRoundingMethod}
                                    amount={lvl.minQualifier}
                                />
                            </td>
                        ) ) }
                    </tr>
                }
                { isBonusEnabled && bonusPayout &&
                    <tr>
                        <td><LocaleString code="levelComparisonTable.header.bonus" /></td>
                        <td colSpan={5}>
                            <PayoutBonusMessage
                                bonusPayoutStructureType={bonusPayout.payoutStructureType}
                                bonusReplaceOptions={bonusReplaceOptions}
                                bonusMessage={bonusMessage}
                            />
                            &nbsp;
                            { bonusPayout.isGoalNeedToAchieve &&
                                <GoalAchievementMessage isMinimumQualifierEnabled={isMinimumQualifierEnabled} />
                            }
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    );
};

LevelComparisonTable.defaultProps = defaultProps;

export default LevelComparisonTable;
